/**
 * Module-level snapshot of the current view.
 *
 * Why module-level (not a React context):
 * - The chat send path needs to read the latest snapshot at the moment the
 *   user hits Send, without depending on render order. A ref outside React
 *   removes any "stale closure" / "did the provider re-render in time"
 *   risk.
 * - Only one page is in the foreground at a time, so there is exactly one
 *   current view across the app — no need for context-scoped isolation.
 * - Setter and reader are stable functions; consumers don't need to
 *   subscribe (chat doesn't care when the view changes, only what was
 *   true at send time).
 *
 * SSR safety: this module is only imported by client components (chat,
 * recap publishers) — no Next.js server boundary touches it.
 */
import type { FocusedEmail, ReplyToNote, ViewContextEnvelope } from "./types"

let current: ViewContextEnvelope | null = null

// Two cross-cutting bits of context that ride ALONGSIDE the page snapshot
// rather than being owned by any one page publisher:
//   - the email the user has focused (askable on any surface)
//   - the AI note the user is replying to
// They are kept in their own module-level slots and merged into the snapshot
// at read time, so a page publisher overwriting `current` never clobbers them
// (and they survive navigation between pages). Both send paths already call
// `readViewContextSnapshot()`, so this is all it takes for them to ride every
// turn.
let focusedEmail: FocusedEmail | null = null
let replyToNote: ReplyToNote | null = null

export function setViewContextSnapshot(
  snapshot: ViewContextEnvelope | null
): void {
  current = snapshot
}

/**
 * The raw PAGE snapshot, without the cross-cutting focusedEmail / replyToNote
 * merge. Used by `useSetViewContext`'s unmount cleanup to test page-snapshot
 * identity — the merged read returns a fresh object, which would break a
 * `=== snapshot` reference check and leak a stale page snapshot.
 */
export function readPageSnapshot(): ViewContextEnvelope | null {
  return current
}

export function readViewContextSnapshot(): ViewContextEnvelope | null {
  if (!focusedEmail && !replyToNote) return current
  // Base off the active page snapshot when present; otherwise synthesize a
  // minimal envelope so the cross-cutting context still reaches the AI on a
  // page with no publisher (e.g. a bare /chat).
  const base: ViewContextEnvelope = current ?? { page: "chat" }
  return {
    ...base,
    ...(focusedEmail ? { focusedEmail } : {}),
    ...(replyToNote ? { replyToNote } : {}),
  }
}

export function clearViewContextSnapshot(): void {
  current = null
}

/** Set (or clear, with `null`) the email the user currently has focused. */
export function setFocusedEmail(email: FocusedEmail | null): void {
  focusedEmail = email
}

export function readFocusedEmail(): FocusedEmail | null {
  return focusedEmail
}

/** Set (or clear, with `null`) the AI note the user is replying to. */
export function setReplyToNote(note: ReplyToNote | null): void {
  replyToNote = note
}

export function readReplyToNote(): ReplyToNote | null {
  return replyToNote
}
